import { api } from '../../../../api';
import type {
  Connection as ApiConnection,
  Cursor,
  Id,
  Task,
  TaskDefinition,
  TaskGroup,
  WorkflowWithData,
} from '../../../../api/types';
import { taskService } from '../../../../services/task-service';
import './theme.css';
import type { EditorApi } from '../../../../modules/workflow/editor-api';
import { createNodeAnimator } from './animation';
import { Connection } from './connection';
import { setupConnectionSync } from './connection-manager';
import {
  type ClipboardCommand,
  type ClipboardTarget,
  createContextMenuItems,
} from './context-menu';
import { createCursorSync } from './cursor-sync';
import { layoutGraph } from './layout';
import { type Node, setupPlugins } from './plugins';
import { TaskGroupNode } from './task-group-node';
import { TaskNode } from './task-node';
import '../task-group-visual';
import { createAnnotationLayer } from './annotations/annotation-layer';
import { createGroupViews } from './group-views';
import { createTaskOps } from './task-ops';
import { createViewControls } from './view-controls';

export async function createEditor(container: HTMLElement): Promise<EditorApi> {
  let currentWorkflowId: Id | undefined;
  let visitorId: string | undefined;
  let syncing = false;
  const emitWorkflowModified = () => {
    container.dispatchEvent(
      new CustomEvent('workflow-modified', {
        bubbles: true,
        composed: true,
      })
    );
  };

  // Load task definitions once for the editor
  let taskDefinitions: TaskDefinition[] = [];
  try {
    const taskData = await taskService.fetchTaskData([
      'name',
      'description',
      'category',
      'inputDescription',
      'outputDescription',
      'configDescription',
      'interaction',
    ]);
    taskDefinitions = taskData.definitions;
  } catch (e) {
    console.error('Failed to load task definitions:', e);
  }

  let editor: ReturnType<typeof setupPlugins>['editor'];
  let area: ReturnType<typeof setupPlugins>['area'];
  let arrange: ReturnType<typeof setupPlugins>['arrange'];
  let accumulating: ReturnType<typeof setupPlugins>['accumulating'];
  let taskOps: ReturnType<typeof createTaskOps> | null = null;
  let annotationLayer: ReturnType<typeof createAnnotationLayer> | null = null;

  const taskNodes = new Map<number, TaskNode>();
  const connectionMap = new Map<number, any>();
  let selectedConnectionApiId: number | undefined;

  const resolveConnectionDetail = (
    apiId: number,
    conn: any
  ): ApiConnection => ({
    id: apiId,
    fromId: Number((conn as any)._kvFromId ?? conn.source),
    toId: Number((conn as any)._kvToId ?? conn.target),
    fromField: String(conn.sourceOutput),
    toField: String(conn.targetInput),
    name:
      (conn as any)._kvName && String((conn as any)._kvName).trim().length > 0
        ? String((conn as any)._kvName)
        : `c${apiId}`,
  });

  const setConnectionSelectedState = (apiId?: number) => {
    selectedConnectionApiId = apiId;
    for (const [id, conn] of connectionMap) {
      const selected = id === apiId;
      (conn as any).selected = selected;
      void area.update('connection', (conn as any).id as any);
    }
  };

  const dispatchConnectionSelected = (apiId: number, conn: any) => {
    container.dispatchEvent(
      new CustomEvent('connection-selected', {
        detail: resolveConnectionDetail(apiId, conn),
        bubbles: true,
        composed: true,
      })
    );
  };

  const clearConnectionSelection = () => {
    if (selectedConnectionApiId == null) return;
    setConnectionSelectedState(undefined);
    container.dispatchEvent(
      new CustomEvent('connection-selection-cleared', {
        bubbles: true,
        composed: true,
      })
    );
  };

  const deleteTaskNode = async (node: TaskNode) => {
    if (!taskOps) return;
    await taskOps.deleteTaskNode(node);
  };

  const exportTask = async (node: TaskNode) => {
    if (!taskOps) return;
    await taskOps.exportTask(node);
  };

  const pasteSelection = async () => {
    if (!taskOps) return;
    let clipboardText = '';
    try {
      clipboardText = await navigator.clipboard.readText();
    } catch (error) {
      console.error('Unable to read clipboard', error);
      return;
    }
    if (annotationLayer?.pasteFromClipboardText(clipboardText)) {
      return;
    }
    await taskOps.pasteTask(clipboardText);
  };

  const getTaskId = (taskNode: TaskNode): number | undefined => {
    const node = taskNode as TaskNode & { apiId?: number };
    const rawId =
      typeof node.apiId === 'number' ? node.apiId : Number(taskNode.id);
    const taskId = Number(rawId);
    return Number.isFinite(taskId) ? taskId : undefined;
  };

  const resolveClipboardTarget = (context: unknown): ClipboardTarget => {
    if (context instanceof TaskNode) {
      const taskId = getTaskId(context);
      if (taskId != null) {
        return { kind: 'task', taskId };
      }
    }
    if (
      context instanceof TaskGroupNode ||
      (typeof context === 'object' && context !== null && 'groupKey' in context)
    ) {
      return { kind: 'group' };
    }
    if (annotationLayer?.hasSelection()) {
      return { kind: 'annotation' };
    }
    return { kind: 'root' };
  };

  const canRunClipboardCommand = (
    command: ClipboardCommand,
    target: ClipboardTarget
  ): boolean => {
    if (command === 'paste') {
      return true;
    }
    return target.kind === 'task' || target.kind === 'annotation';
  };

  const runClipboardCommand = async (
    command: ClipboardCommand,
    target: ClipboardTarget
  ) => {
    if (command === 'paste') {
      await pasteSelection();
      return;
    }
    if (target.kind === 'task') {
      const node = taskNodes.get(target.taskId);
      if (!node) return;
      if (command === 'copy') {
        await exportTask(node);
        return;
      }
      await exportTask(node);
      await deleteTaskNode(node);
      return;
    }
    if (target.kind === 'annotation') {
      if (command === 'copy') {
        await annotationLayer?.copySelectionToClipboard();
      } else {
        await annotationLayer?.cutSelectionToClipboard();
      }
    }
  };

  const copySelection = async (taskId?: number) => {
    const target: ClipboardTarget =
      taskId != null
        ? { kind: 'task', taskId }
        : annotationLayer?.hasSelection()
          ? { kind: 'annotation' }
          : { kind: 'root' };
    await runClipboardCommand('copy', target);
  };

  const cutSelection = async (taskId?: number) => {
    const target: ClipboardTarget =
      taskId != null
        ? { kind: 'task', taskId }
        : annotationLayer?.hasSelection()
          ? { kind: 'annotation' }
          : { kind: 'root' };
    await runClipboardCommand('cut', target);
  };

  const contextMenuItems = createContextMenuItems({
    container,
    getWorkflowId: () => currentWorkflowId,
    deleteTaskNode,
    resolveClipboardTarget,
    canRunClipboardCommand,
    runClipboardCommand,
    addTextAnnotation: () => annotationLayer?.addTextAtPointer(),
    addRectAnnotation: () => annotationLayer?.addRectAtPointer(),
  });

  ({ editor, area, arrange, accumulating } = setupPlugins(
    container,
    contextMenuItems
  ));

  annotationLayer = createAnnotationLayer({
    area,
    container,
    onChange: (annotations) => {
      container.dispatchEvent(
        new CustomEvent('annotations-changed', {
          detail: { annotations },
          bubbles: true,
          composed: true,
        })
      );
    },
  });

  const { centerOnContent } = createViewControls(area, container, taskNodes);
  const groupViews = createGroupViews({
    area,
    editor,
    taskNodes,
    connectionMap,
    getWorkflowId: () => currentWorkflowId,
    deleteTaskGroup: async (workflowId, groupId) => {
      await api.workflows.deleteTaskGroup(workflowId, groupId);
    },
    emitWorkflowModified,
  });
  const {
    renderTaskGroups,
    updateVisibility,
    reset: resetGroupViews,
  } = groupViews;
  const cursorSync = createCursorSync({
    area,
    container,
    getWorkflowId: () => currentWorkflowId,
    getVisitorId: () => visitorId,
    updateRemoteCursor: (workflowId, id, cursor) =>
      api.workflows.updateVisitorCursor(workflowId, id, cursor),
  });
  taskOps = createTaskOps({
    area,
    editor,
    taskNodes,
    connectionMap,
    getWorkflowId: () => currentWorkflowId,
    setSyncing: (value) => {
      syncing = value;
    },
  });

  const { animateNodePosition, cancelAll } = createNodeAnimator(area);

  area.addPipe((context) => {
    const cursorContext = cursorSync.handlePipe(context);
    const groupContext = groupViews.handlePipe(cursorContext);
    // Selection is handled in selector dispatch; no additional node-selected pipe here
    return groupContext;
  });

  area.addPipe((ctx) => {
    if (ctx.type !== 'pointerdown') return ctx;

    const event = (ctx as any)?.data?.event as PointerEvent | undefined;
    const path = event?.composedPath?.() ?? [];
    const target = event?.target as globalThis.Node | null;
    let clickedConnectionApiId: number | undefined;

    for (const [apiId, conn] of connectionMap) {
      const view = area.connectionViews.get((conn as any).id as any);
      if (
        view?.element &&
        (path.includes(view.element) ||
          (target != null && view.element.contains(target)))
      ) {
        clickedConnectionApiId = apiId;
        break;
      }
    }

    if (clickedConnectionApiId != null) {
      const conn = connectionMap.get(clickedConnectionApiId);
      if (conn) {
        setConnectionSelectedState(clickedConnectionApiId);
        dispatchConnectionSelected(clickedConnectionApiId, conn);
      }
      return ctx;
    }

    clearConnectionSelection();
    return ctx;
  });

  const sendNodePosition = (
    taskId: number,
    position: { x: number; y: number }
  ) => {
    taskOps?.sendNodePosition(taskId, position);
  };

  setupConnectionSync(
    editor,
    connectionMap,
    () => currentWorkflowId,
    () => syncing
  );

  // Listen for node translation events from the area plugin
  area.addPipe(async (ctx) => {
    if (syncing) return ctx;
    if (ctx.type === 'nodedragged') {
      const node = (ctx as any).data as any;
      if (node instanceof TaskGroupNode) {
        return ctx;
      }
      const apiId = (node as any)?.apiId;
      if (!Number.isFinite(apiId)) {
        return ctx;
      }
      const position = area.nodeViews.get(node.id)?.position;
      if (position) {
        sendNodePosition(apiId, position);
      }
      void renderTaskGroups();
    }

    return ctx;
  });

  // Double-click detection for task nodes
  // Uses manual click tracking since the area plugin may intercept native dblclick events
  let lastClickTime = 0;
  let lastClickNodeId: number | null = null;
  const DOUBLE_CLICK_DELAY = 300; // ms

  const findWhiteNode = (target: HTMLElement): any => {
    return target.closest('white-node') as any;
  };

  const handleDoubleClickDetection = (target: HTMLElement) => {
    const whiteNode = findWhiteNode(target);
    if (!whiteNode || !whiteNode.data) {
      return null;
    }

    const data = whiteNode.data;
    const rawId = data?.id;
    const id = Number(rawId);
    if (!Number.isFinite(id)) {
      return null;
    }

    const now = Date.now();
    const isDoubleClick =
      lastClickNodeId === id && now - lastClickTime < DOUBLE_CLICK_DELAY;

    if (isDoubleClick) {
      lastClickTime = 0;
      lastClickNodeId = null;
      return { id, data };
    } else {
      lastClickTime = now;
      lastClickNodeId = id;
      return null;
    }
  };

  const dispatchTaskDoubleClick = (id: number, data: any) => {
    container.dispatchEvent(
      new CustomEvent('task-double-clicked', {
        detail: {
          id,
          label: data?.label ?? `Task ${id}`,
          status: data?.status,
          error: data?.error,
        },
        bubbles: true,
        composed: true,
      })
    );
  };

  const handleContainerClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const result = handleDoubleClickDetection(target);
    if (result) {
      dispatchTaskDoubleClick(result.id, result.data);
    }
  };

  const handleContainerPointerDown = (e: PointerEvent) => {
    const target = e.target as HTMLElement;
    const result = handleDoubleClickDetection(target);
    if (result) {
      dispatchTaskDoubleClick(result.id, result.data);
    }
  };

  const handleContainerDoubleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const whiteNode = findWhiteNode(target);
    if (whiteNode?.data) {
      const data = whiteNode.data;
      const rawId = data?.id;
      const id = Number(rawId);
      if (Number.isFinite(id)) {
        dispatchTaskDoubleClick(id, data);
      }
    }
  };

  container.addEventListener('click', handleContainerClick, true);
  container.addEventListener('pointerdown', handleContainerPointerDown, true);
  container.addEventListener('dblclick', handleContainerDoubleClick, true);

  return {
    getPointer: (e: { clientX: number; clientY: number }) => {
      area.area.setPointerFrom(e as any);
      return { ...area.area.pointer };
    },
    destroy: () => {
      cursorSync.destroy();
      container.removeEventListener('click', handleContainerClick, true);
      container.removeEventListener(
        'pointerdown',
        handleContainerPointerDown,
        true
      );
      container.removeEventListener(
        'dblclick',
        handleContainerDoubleClick,
        true
      );
      annotationLayer?.destroy();
      accumulating?.destroy?.();
      cancelAll();
      area.destroy();
    },
    loadWorkflow: async (wf: WorkflowWithData) => {
      syncing = true;
      try {
        await editor.clear();
        annotationLayer?.setAnnotations(wf.annotations ?? []);

        // Create a node per task, store mapping
        taskNodes.clear();
        resetGroupViews(wf.taskGroups ?? []);
        connectionMap.clear();
        for (const t of wf.tasks) {
          const icon = await taskService.getIcon(t.type);
          const node = new TaskNode(t, taskDefinitions, wf.id, icon);
          // keep a reference to backend task id on the node for later API calls
          (node as any).apiId = t.id;
          await editor.addNode(node as unknown as Node);
          taskNodes.set(t.id, node);
          if (t.visual) {
            await area.translate(node.id, t.visual);
          }
        }
        // Draw connections using direct task IDs
        for (const c of wf.connections ?? []) {
          const src = taskNodes.get(c.fromId);
          const dst = taskNodes.get(c.toId);
          const fromField = c.fromField;
          const toField = c.toField;

          if (!src || !dst || !fromField || !toField) {
            console.warn(
              'Skipping connection due to missing source/target or fields',
              {
                c,
                src: !!src,
                dst: !!dst,
                fromField,
                toField,
              }
            );
            continue;
          }

          // Ensure handles exist on nodes before creating connection
          const hasFrom = src.outputs && fromField in src.outputs;
          const hasTo = dst.inputs && toField in dst.inputs;

          if (!hasFrom || !hasTo) {
            console.warn('Skipping connection due to missing handles', {
              c,
              fromField,
              toField,
              hasFrom,
              hasTo,
              srcOutputs: Object.keys(src.outputs || {}),
              dstInputs: Object.keys(dst.inputs || {}),
            });
            continue;
          }

          try {
            const conn = new Connection(
              src as any,
              fromField,
              dst as any,
              toField
            ) as any;
            conn.apiId = c.id;
            (conn as any)._kvFromId = c.fromId;
            (conn as any)._kvToId = c.toId;
            (conn as any)._kvName = c.name;
            (conn as any).selected = false;
            await editor.addConnection(conn);
            connectionMap.set(c.id, conn);
          } catch (e) {
            console.error('Failed to add connection', e);
          }
        }
        setConnectionSelectedState(undefined);
        void renderTaskGroups();
        updateVisibility();
        await centerOnContent();
      } finally {
        syncing = false;
      }
    },
    addTask: async (task: Task) => {
      syncing = true;
      if (!currentWorkflowId) {
        console.error('No current workflow ID set');
        syncing = false;
        return;
      }
      const taskId = task.id;
      if (taskNodes.has(taskId) || editor.getNode(String(taskId))) {
        syncing = false;
        return;
      }
      const icon = await taskService.getIcon(task.type);
      const node = new TaskNode(task, taskDefinitions, currentWorkflowId, icon);
      (node as any).apiId = taskId;
      await editor.addNode(node as unknown as Node);
      taskNodes.set(taskId, node);
      if (task.visual) {
        await area.translate(node.id, task.visual);
      }
      syncing = false;
      await renderTaskGroups();
      updateVisibility();
    },
    removeTask: async (taskId: number) => {
      const node = taskNodes.get(taskId);
      if (node) {
        syncing = true;
        await editor.removeNode(node.id);
        taskNodes.delete(taskId);
        for (const [id, conn] of connectionMap) {
          if (conn.source === node || conn.target === node) {
            connectionMap.delete(id);
          }
        }
        syncing = false;
        void renderTaskGroups();
        updateVisibility();
      }
    },
    updateTask: async (task: Task) => {
      const node = taskNodes.get(task.id);
      if (!node) return;
      let needsRefresh = false;
      if (task.label !== undefined) {
        node.updateLabel(task.label);
        needsRefresh = true;
      }
      if (task.approvalGuards !== undefined) {
        node.updateApprovalGuards(task.approvalGuards);
      }
      if (task.visual) {
        syncing = true;
        let moved = false;
        try {
          await animateNodePosition(node.id, task.visual);
          moved = true;
        } catch (error) {
          console.error('Failed to animate node position:', error);
          // Fallback to instant translation if animation fails
          await area.translate(node.id, task.visual);
          moved = true;
        } finally {
          syncing = false;
        }
        if (moved) {
          void renderTaskGroups();
          updateVisibility();
        }
      }
      if (needsRefresh) {
        await area.update('node', node.id);
      }
    },
    updateTaskConfig: (taskId: number, config: Record<string, unknown>) => {
      const node = taskNodes.get(taskId);
      if (!node) return;
      node.updateConfig(config).map(async (id) => {
        await area.update('control', id);
      });
    },
    getTaskConfig: (taskId: number) => {
      const node = taskNodes.get(taskId);
      return node ? { ...node.config } : undefined;
    },
    updateTaskApprovalGuards: (taskId, approvalGuards) => {
      const node = taskNodes.get(taskId);
      node?.updateApprovalGuards(approvalGuards);
    },
    getTaskApprovalGuards: (taskId: number) => {
      const node = taskNodes.get(taskId);
      return node ? { ...node.approvalGuards } : undefined;
    },
    getTaskLabel: (taskId: number) => {
      const node = taskNodes.get(taskId);
      return node?.taskLabel ?? null;
    },
    getTaskDefinition: (taskId: number) => {
      const node = taskNodes.get(taskId);
      if (!node) return undefined;
      return taskDefinitions.find((def) => def.name === node.taskType);
    },
    getTaskValues: (taskId: number) => {
      const node = taskNodes.get(taskId);
      if (!node) return {};
      const input: Record<string, unknown> = {};
      const output: Record<string, unknown> = {};
      for (const [key, inp] of Object.entries(node.inputs ?? {})) {
        input[key] = (inp as any).value;
      }
      for (const [key, out] of Object.entries(node.outputs ?? {})) {
        output[key] = (out as any).value;
      }
      return { input, output };
    },
    updateTaskState: async (
      taskId: number,
      state: {
        input?: Record<string, unknown>;
        output?: Record<string, unknown>;
        status?: string;
        error?: string | null;
        disabled?: boolean;
        interactionPending?: boolean;
      }
    ) => {
      const node = taskNodes.get(taskId);
      if (!node) return;

      node.updateValues(state.input, state.output);
      (node as any).status = state.status;
      (node as any).error = state.error;
      if (state.disabled !== undefined) {
        (node as any).disabled = state.disabled;
      }
      await area.update('node', node.id);
    },
    addConnection: async (c: {
      id: number;
      fromId: number;
      toId: number;
      fromField: string;
      toField: string;
      name: string;
    }) => {
      if (connectionMap.has(c.id)) {
        console.warn(`Connection with ID ${c.id} already exists`);
        updateVisibility();
        return;
      }
      syncing = true;
      const src = taskNodes.get(c.fromId);
      const dst = taskNodes.get(c.toId);
      if (!src || !dst) {
        console.error(
          `Cannot add connection: source task ${c.fromId} found: ${!!src}, destination task ${c.toId} found: ${!!dst}`
        );
        syncing = false;
        return;
      }
      // If a local connection already exists visually (e.g., created locally before server echoed it),
      // attach the apiId and map it instead of creating a duplicate.
      try {
        const existing = Array.from(editor.getConnections?.() ?? []).find(
          (editorConn) => {
            const ec = editorConn as any;
            return (
              ec?.source === String(c.fromId) &&
              ec?.target === String(c.toId) &&
              String(ec?.sourceOutput) === String(c.fromField) &&
              String(ec?.targetInput) === String(c.toField)
            );
          }
        );
        if (existing) {
          (existing as any).apiId = c.id;
          (existing as any)._kvFromId = c.fromId;
          (existing as any)._kvToId = c.toId;
          (existing as any)._kvName = c.name;
          (existing as any).selected = false;
          connectionMap.set(c.id, existing as any);
          syncing = false;
          updateVisibility();
          return;
        }
      } catch {
        // ignore scan errors and proceed to create
      }
      const conn = new Connection(
        src as any,
        c.fromField,
        dst as any,
        c.toField
      ) as any;
      conn.apiId = c.id;
      (conn as any)._kvFromId = c.fromId;
      (conn as any)._kvToId = c.toId;
      (conn as any)._kvName = c.name;
      (conn as any).selected = false;
      await editor.addConnection(conn);
      connectionMap.set(c.id, conn);
      syncing = false;
      updateVisibility();
    },
    removeConnection: async (id: number) => {
      const conn = connectionMap.get(id);
      if (!conn) {
        // If it's already gone locally, treat as idempotent and exit quietly.
        // Try a best-effort fallback search by apiId in case map is stale.
        for (const [mapKey, connection] of connectionMap) {
          if ((connection as any).apiId === id) {
            syncing = true;
            try {
              const connectionId = (connection as any).id;
              await editor.removeConnection(connectionId);
            } catch {
              try {
                await editor.removeConnection(connection as any);
              } catch {
                // Ignore – connection might already be removed in the editor
              }
            }
            connectionMap.delete(mapKey);
            if (selectedConnectionApiId === mapKey) {
              clearConnectionSelection();
            }
            syncing = false;
            return;
          }
        }
        // Nothing to do – already removed.
        return;
      }
      syncing = true;
      try {
        const connectionId = (conn as any).id;
        await editor.removeConnection(connectionId);
      } catch (_error) {
        // If editor cannot find it, it was likely already removed. Try best-effort fallback, then ignore.
        try {
          const editorConnections = Array.from(editor.getConnections?.() ?? []);
          const matchingConnection = editorConnections.find((editorConn) => {
            const ec = editorConn as any;
            const mc = conn as any;
            return (
              ec?.apiId === mc?.apiId ||
              (ec?.source === mc?.source && ec?.target === mc?.target)
            );
          });
          if (matchingConnection) {
            try {
              await editor.removeConnection((matchingConnection as any).id);
            } catch {
              // ignore – already removed or not present
            }
          }
        } catch {
          // ignore fallback errors
        }
      }
      connectionMap.delete(id);
      if (selectedConnectionApiId === id) {
        clearConnectionSelection();
      }
      syncing = false;
    },
    updateConnectionName: (id: number, name: string) => {
      const conn = connectionMap.get(id);
      if (!conn) return;
      (conn as any)._kvName = name;
      if (selectedConnectionApiId === id) {
        dispatchConnectionSelected(id, conn);
      }
    },
    setWorkflowId: (id: Id) => {
      currentWorkflowId = id;
    },
    setVisitorId: (id: string) => {
      visitorId = id;
    },
    updateVisitorCursor: (id: string, color: string, cursor: Cursor) => {
      cursorSync.updateVisitorCursor(id, color, cursor);
    },
    removeVisitorCursor: (id: string) => {
      cursorSync.removeVisitorCursor(id);
    },
    clearVisitorCursors: () => {
      cursorSync.clearVisitorCursors();
    },
    setCursorUpdateInterval: (ms: number) => {
      cursorSync.setCursorUpdateInterval(ms);
    },
    layout: async () => {
      await layoutGraph(editor, arrange, taskNodes, area, sendNodePosition);
      void renderTaskGroups();
      updateVisibility();
    },
    copySelection,
    cutSelection,
    deleteTask: async (taskId: number) => {
      const node = taskNodes.get(taskId);
      if (node) {
        await deleteTaskNode(node);
      }
    },
    pasteSelection,
    hasSelectedAnnotation: () => annotationLayer?.hasSelection() ?? false,
    setTaskGroups: async (groups: TaskGroup[]) => {
      await groupViews.setTaskGroups(groups);
    },
    setSelectedGroup: (groupId?: string | number | Array<string | number>) => {
      groupViews.setSelectedGroup(groupId);
    },
    setAnnotations: (annotations) => {
      annotationLayer?.setAnnotations(annotations ?? []);
    },
  };
}
